import React from 'react';
import { Box, Typography, Paper, TextField, InputAdornment, FormControl, Select, MenuItem, Autocomplete, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem as MenuItemMUI, Chip, Snackbar, Alert, LinearProgress, FormHelperText, Checkbox, FormControlLabel, Divider } from '@mui/material';
import { Search, Visibility, CheckCircle, DoNotDisturbOn, ReportProblem, InfoOutlined, DoneAll, MoreVert, ContentCopy, Add, UploadFile, PictureAsPdf, Delete, Settings } from '@mui/icons-material';
import driveSvg from '../assets/images/google-drive.svg';
import { listMaterialApprovals, createMaterialApproval, uploadMaterialApprovalAttachment, quickCreateKzmUpload, getKzmDetails, type MaterialApprovalDto, type MaterialApprovalBranch, reviseKzm, deleteKzm, sendKzmEmail } from '../services/materialApprovalsService';
import { API_BASE_URL } from '../services/api';
import { getAllProjects } from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';
import { listUsersForBusinessForms, type AppUser } from '../services/userService';
import { getKzmSettings, type KzmSettingsDto } from '../services/kzmSettingsService';

const branches: MaterialApprovalBranch[] = ['Budowlana','Sanitarna','Elektryczna','Teletechniczna'];
const CARD_STATUSES = ['Przygotowana','Sprawdzona','Wysłana','Do poprawienia'];
const APPROVAL_STATUSES = ['Zatwierdzone bez uwag','Zatwierdzone z uwagami','Odrzucone'];

const MaterialApprovalPage: React.FC = () => {
  const [q, setQ] = React.useState('');
  const { user } = useAuth();
  const [branch, setBranch] = React.useState<'' | MaterialApprovalBranch>('');
  const [projectId, setProjectId] = React.useState<string>('');
  const [preparedBy, setPreparedBy] = React.useState<string>('');
  const [month, setMonth] = React.useState<string>(''); // YYYY-MM
  const [status, setStatus] = React.useState<string>('');
  const [cardStatusFilter, setCardStatusFilter] = React.useState<string>('');
  const [approvalStatusFilter, setApprovalStatusFilter] = React.useState<string>('');

  const [rows, setRows] = React.useState<MaterialApprovalDto[]>([]);
  const [projects, setProjects] = React.useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = React.useState<string[]>([]);
  const [usersMap, setUsersMap] = React.useState<Record<string, AppUser>>({});

  const [preview, setPreview] = React.useState<MaterialApprovalDto | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = React.useState<string | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [menuRow, setMenuRow] = React.useState<MaterialApprovalDto | null>(null);

  // Dodawanie KZM – stan formularza (UI-only)
  const [addOpen, setAddOpen] = React.useState(false);
  const [addProjectId, setAddProjectId] = React.useState<string>('');
  const [addBranch, setAddBranch] = React.useState<'' | MaterialApprovalBranch>('');
  const [addMaterialName, setAddMaterialName] = React.useState('');
  const [addInstallPlace, setAddInstallPlace] = React.useState('');
  const [addCompliance, setAddCompliance] = React.useState<'Zgodny z projektem'|'Równoważny'|'Nie określony w projekcie'|''>('');
  const [attachments, setAttachments] = React.useState<Array<{ id: string; name: string; url: string; driveId?: string; fileObject?: File; description?: string }>>([]);
  const [saving, setSaving] = React.useState(false);
  const [activeAttachmentId, setActiveAttachmentId] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [snack, setSnack] = React.useState<{ open: boolean; msg: string; severity: 'info'|'success'|'error' }>(()=>({ open: false, msg: '', severity: 'info' }));
  // Jeśli poprzednio zapisano formularz – zresetuj przy następnym otwarciu (tylko dla trybu Dodaj)
  const [resetAddOnOpen, setResetAddOnOpen] = React.useState<boolean>(false);
  const resetAddForm = React.useCallback(() => {
    setAddProjectId('');
    setAddBranch('' as any);
    setAddMaterialName('');
    setAddInstallPlace('');
    setAddCompliance('' as any);
    setAttachments([]);
    setActiveAttachmentId(null);
  }, []);
  const showSnack = (severity: 'info'|'success'|'error', msg: string) => setSnack({ open: true, severity, msg });

  // Wysyłka KZM – UI-only
  const [sendOpen, setSendOpen] = React.useState(false);
  const [sendFrom, setSendFrom] = React.useState<string>('');
  const [sendTo, setSendTo] = React.useState<string>('');
  const [sendCc, setSendCc] = React.useState<string>('');
  const [sendSubject, setSendSubject] = React.useState<string>('');
  const [sendBody, setSendBody] = React.useState<string>('');
  const [sendAttachments, setSendAttachments] = React.useState<Array<{ id: string; name: string; description?: string; driveId?: string; sizeBytes?: number }>>([]);
  const [sendIncludeAll, setSendIncludeAll] = React.useState<boolean>(true);
  const [sendAttachmentsSizeMB, setSendAttachmentsSizeMB] = React.useState<number>(0);
  const [sendLoading, setSendLoading] = React.useState<boolean>(false);
  const [sendBranch, setSendBranch] = React.useState<string>('');

  // Tryb zmiany szerokości kolumn + zapamiętywanie per użytkownik (analogicznie do Faktur/Projektów)
  const headerCellSx: any = React.useMemo(() => ({
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: (t: any) => (t.palette.mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.06)'),
    whiteSpace: 'nowrap'
  }), []);
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    sel: 44,
    id: 160,
    prepared: 110,
    desc: 260,
    project: 160,
    cardStatus: 120,
    cardStatusAt: 120,
    approvalIcon: 44,
    approvalAt: 110,
    preview: 100,
    actions: 44,
  }), []);
  const STORAGE_KEY = React.useMemo(() => `materialsApprovalsColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getColW = React.useCallback((key: string) => `${columnWidths[key] ?? defaultColumnWidths[key] ?? 40}px`, [columnWidths, defaultColumnWidths]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(16, dragCol.startW + dx);
      setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
    };
  }, [dragCol]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none', position: 'relative' }}>
      {node}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => setDragCol({ key: keyId, startX: e.clientX, startW: columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 40 })}
          sx={{ position: 'absolute', right: 0, top: 0, width: '6px', height: '100%', cursor: 'col-resize', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}
        />
      ) : null}
    </TableCell>
  );

  // Prefill edycji z menuRow
  React.useEffect(() => {
    (async () => {
      if (addOpen && menuRow) {
        try {
          const det = await getKzmDetails(menuRow.kzm_id || String(menuRow.id));
          const k = det?.kzm || {};
          setAddProjectId(k.project_id || menuRow.project_id || '');
          setAddBranch(((k.material_branch || menuRow.branch) as any) || '');
          setAddMaterialName(k.material_trade_name || menuRow.material_name || '');
          setAddInstallPlace(k.install_place || '');
          setAddCompliance((k.compliance || '') as any);
          // Załaduj załączniki jako listę UI (bez fizycznych plików)
          const atts = Array.isArray(det?.attachments) ? det.attachments : [];
          const mapped = atts.map((a:any, idx:number)=>({ id: a.kzm_attachment_id || `Z${idx+1}`, name: a.file_name || `Z${idx+1}.pdf`, url: a.google_drive_file_url || '', driveId: a.google_drive_file_id || '', fileObject: undefined, description: a.file_description || '' }));
          setAttachments(mapped);
          if (mapped.length) setActiveAttachmentId(mapped[0].id);
        } catch {
          // fallback minimalny
          setAddProjectId(menuRow.project_id || '');
          setAddBranch((menuRow.branch as any) || '');
          setAddMaterialName(menuRow.material_name || '');
        }
      }
    })();
  }, [addOpen, menuRow]);

  // Pomocniczo: policz sumę MB załączników (z DB; jeśli brak, spróbuj pobrać HEAD z proxy)
  const recomputeAttachmentsSize = React.useCallback(async (atts: Array<{ driveId?: string; sizeBytes?: number }>) => {
    let total = 0;
    for (const a of atts) {
      if (a.sizeBytes && Number.isFinite(a.sizeBytes)) { total += a.sizeBytes as number; continue; }
      if (!a.driveId) continue;
      try {
        const url = `${API_BASE_URL}/kzms/attachments/${encodeURIComponent(a.driveId)}/content`;
        const res = await fetch(url, { method: 'HEAD' as any });
        const len = parseInt(res.headers.get('Content-Length') || res.headers.get('content-length') || '0', 10);
        if (Number.isFinite(len)) total += len;
      } catch {}
    }
    setSendAttachmentsSizeMB(Math.max(0, Math.round((total / (1024*1024)) * 10) / 10));
  }, []);

  // Prefill dialogu wysyłki
  React.useEffect(() => {
    (async () => {
      if (!(sendOpen && menuRow)) return;
      try {
        const det = await getKzmDetails(menuRow.kzm_id || String(menuRow.id));
        const atts = Array.isArray(det?.attachments) ? det.attachments : [];
        const mapped = atts.map((a: any, idx: number) => ({
          id: a.kzm_attachment_id || `Z${idx+1}`,
          name: a.file_name || `Z${idx+1}.pdf`,
          description: a.file_description || '',
          driveId: a.google_drive_file_id || '',
          sizeBytes: a.file_size || undefined
        }));
        setSendAttachments(mapped);
        recomputeAttachmentsSize(mapped);
        // Nadawca – domyślnie powiadomienia
        setSendFrom('powiadomienia@unitresco.com');
        const k = det?.kzm || {};
        setSendBranch(k.material_branch || menuRow.branch || '');
        const folderUrl = k.drive_folder_id ? `https://drive.google.com/drive/folders/${k.drive_folder_id}` : (menuRow.google_drive_file_url || '');
        const subj = `KZM ${menuRow.kzm_id || menuRow.id} — ${menuRow.material_name || ''}`.trim();
        setSendSubject(subj);
        setSendBody([
          'Szanowni Państwo,',
          '',
          `Przesyłam do akceptacji kartę materiałową ${menuRow.kzm_id || menuRow.id} — ${menuRow.material_name || ''}.`,
          '',
          'Dokumenty i załączniki znajdują się w folderze Google Drive:',
          folderUrl || '(brak linku do folderu)',
          '',
          'W razie pytań pozostaję do dyspozycji.'
        ].join('\n'));
        // Uzupełnij DO/DW z kzm_settings, jeśli dostępne
        try {
          const projectIdForSettings = k.project_id || menuRow.project_id || '';
          if (projectIdForSettings) {
            const settings: KzmSettingsDto | null = await getKzmSettings(projectIdForSettings);
            if (settings) {
              const sanitize = (s?: string | null) => String(s || '')
                .split(',')
                .map(x => x.trim())
                .filter(Boolean);
              const uniq = (arr: string[]) => Array.from(new Set(arr));
              const join = (arr: string[]) => uniq(arr).join(', ');
              let toList: string[] = [];
              let ccList: string[] = [];
              const op = sanitize(settings.ordering_party_email);
              const bld = sanitize(settings.inspector_build_email);
              const san = sanitize(settings.inspector_sanitary_email);
              const el  = sanitize(settings.inspector_electrical_email);
              const tel = sanitize(settings.inspector_telecom_email);
              const ccB = sanitize(settings.cc_build);
              const ccS = sanitize(settings.cc_sanitary);
              const ccE = sanitize(settings.cc_electrical);
              const ccT = sanitize(settings.cc_telecom);
              const br = (k.material_branch || menuRow.branch || '') as string;
              switch (br) {
                case 'Budowlana':
                  toList = [...op, ...bld];
                  ccList = [...tel, ...san, ...el, ...ccB];
                  break;
                case 'Sanitarna':
                  toList = [...op, ...san];
                  ccList = [...bld, ...tel, ...el, ...ccS];
                  break;
                case 'Elektryczna':
                  toList = [...op, ...el];
                  ccList = [...bld, ...tel, ...san, ...ccE];
                  break;
                case 'Teletechniczna':
                  toList = [...op, ...tel];
                  ccList = [...bld, ...san, ...el, ...ccT];
                  break;
                default:
                  toList = [...op];
                  ccList = [...bld, ...san, ...el, ...tel];
              }
              setSendTo(join(toList));
              setSendCc(join(ccList));
            } else {
              setSendTo('');
              setSendCc('');
            }
          } else {
            setSendTo('');
            setSendCc('');
          }
        } catch {
          setSendTo('');
          setSendCc('');
        }
      } catch {
        setSendAttachments([]);
        setSendAttachmentsSizeMB(0);
      }
    })();
  }, [sendOpen, menuRow, user, recomputeAttachmentsSize]);

  const branchChipColor = (b?: string): 'default'|'primary'|'secondary'|'success'|'warning'|'error'|'info' => {
    switch (String(b || '')) {
      case 'Budowlana':
        return 'primary';
      case 'Sanitarna':
        return 'success';
      case 'Elektryczna':
        return 'warning';
      case 'Teletechniczna':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Po udanym zapisie wyczyść formularz przy kolejnym otwarciu trybu Dodaj
  React.useEffect(() => {
    if (addOpen && !menuRow && resetAddOnOpen) {
      resetAddForm();
      setResetAddOnOpen(false);
    }
  }, [addOpen, menuRow, resetAddOnOpen, resetAddForm]);

  React.useEffect(() => {
    listMaterialApprovals().then((d) => setRows(d || [])).catch(() => setRows([]));
    getAllProjects().then((ps: any[]) => setProjects(ps.map((p: any) => ({ id: String(p.project_id), name: String(p.project_name || '') })))).catch(()=>setProjects([]));
    listUsersForBusinessForms().then((list) => {
      setUsers(Array.from(new Set(list.map(u => u.display_name || '').filter(Boolean))) as string[]);
      const map: Record<string, AppUser> = {}; list.forEach(u => { map[u.user_id] = u; }); setUsersMap(map);
    }).catch(()=>{});
  }, []);

  // Obsłuż wejście z parametrem ?send=KZM_ID aby otworzyć od razu okno wysyłki
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sendId = params.get('send');
      if (sendId) {
        setMenuRow({ id: sendId, kzm_id: sendId } as any);
        setSendOpen(true);
      }
    } catch {}
  }, []);

  const months = React.useMemo(() => {
    const now = new Date();
    const items: { key: string; label: string }[] = [];
    for (let i=0;i<18;i++) {
      const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      items.push({ key, label: d.toLocaleDateString('pl-PL', { year: 'numeric', month: 'long' }) });
    }
    return items;
  }, []);

  const filtered = rows.filter((r) => {
    const matchesQ = `${r.id} ${r.project_id} ${r.project_name || ''} ${r.material_name}`.toLowerCase().includes(q.toLowerCase());
    const matchesBranch = branch ? r.branch === branch : true;
    const matchesProject = projectId ? r.project_id === projectId : true;
    const matchesPreparedBy = preparedBy ? (String(r.prepared_by_name || '') === preparedBy) : true;
    const matchesMonth = month ? String(r.prepared_at || '').startsWith(month) : true;
    const matchesCardStatus = cardStatusFilter ? String(r.card_status || r.status || '') === cardStatusFilter : true;
    const matchesApprovalStatus = approvalStatusFilter ? String(r.approval_status || '') === approvalStatusFilter : true;
    return matchesQ && matchesBranch && matchesProject && matchesPreparedBy && matchesMonth && matchesCardStatus && matchesApprovalStatus;
  });

  // Ikony statusów
  const approvalStatusIcon = (s?: string) => {
    const title = `Status zatwierdzenia: ${s || '—'}`;
    if (s === 'Akceptacja bez uwag') return <Tooltip title={title} arrow><CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /></Tooltip>;
    if (s === 'Akceptacja z uwagami') return <Tooltip title={title} arrow><ReportProblem sx={{ fontSize: 18, color: '#FFA000' }} /></Tooltip>;
    if (s === 'Brak akceptacji') return <Tooltip title={title} arrow><DoNotDisturbOn sx={{ fontSize: 18, color: 'error.main' }} /></Tooltip>;
    return <Tooltip title={title} arrow><InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} /></Tooltip>;
  };
  const cardStatusChip = (s?: string) => {
    const label = s || '—';
    switch (label) {
      // Nowe statusy z SQL
      case 'Przygotowany':
        return <Chip label={label} size="small" color="info" />;
      case 'Gotowy do wysłania':
        return <Chip label={label} size="small" color="primary" />;
      case 'Wysłano do akceptacji':
        return (
          <Tooltip title="Wysłano do akceptacji" arrow>
            <Chip label="Wysłano" size="small" sx={{ bgcolor: '#FBC02D', color: '#fff' }} />
          </Tooltip>
        );
      case 'Wymaga korekty':
        return <Chip label={label} size="small" color="warning" />;
      case 'Zatwierdzono':
        return <Chip label={label} size="small" color="success" />;
      case 'Zatwierdzono z uwagami':
        return <Chip label={label} size="small" color="warning" variant="outlined" />;
      case 'Odrzucono':
        return <Chip label={label} size="small" color="error" />;
      case 'Anulowano':
        return <Chip label={label} size="small" variant="outlined" />;
      // Wsteczna kompatybilność ze starymi etykietami UI
      case 'Przygotowana':
        return <Chip label={label} size="small" color="info" />;
      case 'Sprawdzona':
        return <Chip label={label} size="small" color="warning" />;
      case 'Wysłana':
        return <Chip label={label} size="small" color="primary" />;
      case 'Do poprawienia':
        return <Chip label={label} size="small" color="error" />;
      default:
        return <Chip label={label} size="small" variant="outlined" />;
    }
  };

  const renderMenu = () => (
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
      open={Boolean(menuPosition)}
      keepMounted
      onClose={() => { setMenuPosition(null); setMenuRow(null); }}
    >
      <MenuItemMUI onClick={() => { if (menuRow) setSendOpen(true); setMenuPosition(null); }}>Wyślij KZM</MenuItemMUI>
      <MenuItemMUI onClick={() => { if (menuRow) setAddOpen(true); setMenuPosition(null); /* prefill handled below */ }}>Edytuj</MenuItemMUI>
      <MenuItemMUI onClick={() => { if (menuRow) { const kid = encodeURIComponent(menuRow.kzm_id || String(menuRow.id)); const pdf = (menuRow as any)?.generated_kzm_pdf_url ? `?pdf=${encodeURIComponent((menuRow as any).generated_kzm_pdf_url)}` : ''; const path = `/app/materials_approvals/${kid}${pdf}`; const url = `${window.location.origin}${path}`; try { window.open(url, '_blank', 'noopener,noreferrer'); } catch { window.location.href = path; } } setMenuPosition(null); setMenuRow(null); }}>Okno zatwierdzenia</MenuItemMUI>
    </Menu>
  );

  // Ustal podgląd PDF dla modalu "Podgląd" – taki sam jak w oknie zatwierdzenia
  React.useEffect(() => {
    (async () => {
      if (!preview) { setPreviewPdfUrl(null); return; }
      // 1) Spróbuj z listy: generated_kzm_pdf_url
      const direct = (preview as any)?.generated_kzm_pdf_url || '';
      const convertToProxy = (url: string) => {
        try {
          const m1 = String(url).match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
          const m2 = String(url).match(/id=([a-zA-Z0-9_-]+)/);
          const m3 = String(url).match(/\/d\/([a-zA-Z0-9_-]+)/);
          const fileId = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]) || null;
          return fileId ? `${API_BASE_URL}/kzms/attachments/${encodeURIComponent(fileId)}/content` : (url || null);
        } catch { return url || null; }
      };
      if (direct) { setPreviewPdfUrl(convertToProxy(direct)); return; }
      // 2) Fallback: pobierz szczegóły KZM po kzm_id i użyj pola z DB lub GDocs preview
      try {
        const det: any = await getKzmDetails(preview.kzm_id || String(preview.id));
        const pdf = det?.kzm?.generated_kzm_pdf_url || '';
        if (pdf) { setPreviewPdfUrl(convertToProxy(pdf)); return; }
        const gdoc = det?.kzm?.generated_kzm_google_doc_id || '';
        if (gdoc) {
          setPreviewPdfUrl(convertToProxy(`https://docs.google.com/document/d/${gdoc}/preview`));
          return;
        }
      } catch {}
      setPreviewPdfUrl(null);
    })();
  }, [preview]);

  // Nasłuchuj zmian decyzji z innej karty (storage event) i odśwież listę
  React.useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'kzm_decision_updated') {
        listMaterialApprovals().then((d) => setRows(d || [])).catch(()=>{});
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const recentProjects = React.useMemo(() => {
    // Projekty posortowane malejąco po prefiksie numerycznym (np. 2024-15 > 2023-11)
    return [...projects].sort((a, b) => {
      const pa = parseInt(String(a.id).split('-')[0]);
      const pb = parseInt(String(b.id).split('-')[0]);
      if (Number.isNaN(pa) || Number.isNaN(pb)) return 0;
      return pb - pa;
    });
  }, [projects]);

  const handleAddFiles = (files: File[]) => {
    const next: Array<{ id: string; name: string; url: string; fileObject?: File }> = [];
    for (const f of files) {
      try {
        const url = URL.createObjectURL(f);
        next.push({ id: `${Date.now()}-${f.name}-${Math.random()}`, name: f.name, url, fileObject: f });
      } catch {}
    }
    setAttachments(prev => {
      const merged = [...prev, ...next];
      if (!activeAttachmentId && merged.length) setActiveAttachmentId(merged[0].id);
      return merged;
    });
  };

  const activeAttachment = React.useMemo(() => attachments.find(a => a.id === activeAttachmentId) || null, [attachments, activeAttachmentId]);

  // Zwolnij obiekty URL dopiero przy zamknięciu okna (nie przy każdej zmianie listy)
  React.useEffect(() => {
    if (!addOpen) return;
    return () => {
      try { attachments.forEach(a => { try { URL.revokeObjectURL(a.url); } catch {} }); } catch {}
    };
  }, [addOpen]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 2, gap: 2, flexWrap:'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Karty materiałowe</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={()=>setAddOpen(true)}>Dodaj KZM</Button>
      </Box>

      <Box sx={{ display:'flex', gap: 1, alignItems:'center', mb: 2, flexWrap:'nowrap', pr: 1, '& > *': { flexShrink: 1 }, '& > :last-child': { mr: 1 } }}>
        <TextField size="small" placeholder="Szukaj" value={q} onChange={(e)=>setQ(e.target.value)} sx={{ width: 140 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }} />
        <FormControl size="small" sx={{ width: 150, flexShrink: 1 }}>
          <Select value={branch} displayEmpty onChange={(e)=>setBranch((e.target.value || '') as any)} renderValue={(v)=> v ? `Branża: ${v}` : 'Branża'}>
            <MenuItem value=""><em>Wszystkie</em></MenuItem>
            {branches.map(b => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
          </Select>
        </FormControl>
        <Autocomplete size="small" sx={{ width: 220, flexShrink: 1 }} options={recentProjects} getOptionLabel={(p)=>`${p.id} ${p.name}`} value={recentProjects.find(p => p.id===projectId) || null} onChange={(_, val)=>setProjectId(val ? val.id : '')} renderInput={(params)=>(<TextField {...params} placeholder="Projekt" />)} isOptionEqualToValue={(a,b)=>a.id===b.id} clearOnBlur={false} />
        <FormControl size="small" sx={{ width: 170, flexShrink: 1 }}>
          <Select value={preparedBy} displayEmpty onChange={(e)=>setPreparedBy(String(e.target.value))} renderValue={(v)=> v ? `Przygotował: ${v}` : 'Przygotował'}>
            <MenuItem value=""><em>Wszyscy</em></MenuItem>
            {users.map(u => (<MenuItem key={u} value={u}>{u}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 150, flexShrink: 1 }}>
          <Select value={month} displayEmpty onChange={(e)=>setMonth(String(e.target.value))} renderValue={(v)=> v ? (months.find(m=>m.key===v)?.label || 'Miesiąc') : 'Miesiąc'}>
            <MenuItem value=""><em>Wszystkie miesiące</em></MenuItem>
            {months.map(m => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 170, flexShrink: 1, '& .MuiSelect-select': { pr: 5 } }}>
          <Select value={cardStatusFilter} displayEmpty onChange={(e)=>setCardStatusFilter(String(e.target.value))} renderValue={(v)=> v ? `Status Karty: ${v}` : 'Status Karty'}>
            <MenuItem value=""><em>Wszystkie</em></MenuItem>
            {CARD_STATUSES.map(s => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 200, flexShrink: 1, '& .MuiSelect-select': { pr: 5 } }}>
          <Select value={approvalStatusFilter} displayEmpty onChange={(e)=>setApprovalStatusFilter(String(e.target.value))} renderValue={(v)=> v ? `Status zatw.: ${v}` : 'Status zatwierdzenia'}>
            <MenuItem value=""><em>Wszystkie</em></MenuItem>
            {APPROVAL_STATUSES.map(s => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', maxHeight: 520, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { padding: '4px 6px' } }}>
          <TableHead>
            <TableRow>
              {renderResizableTh('sel', null as any)}
              {renderResizableTh('id', 'ID')}
              {renderResizableTh('prepared', 'Przygotowano')}
              {renderResizableTh('desc', 'Opis')}
              {renderResizableTh('project', 'Projekt')}
              {renderResizableTh('cardStatus', 'Status Karty', 'right')}
              {renderResizableTh('cardStatusAt', 'Data statusu')}
              {renderResizableTh('approvalIcon', null as any, 'center')}
              {renderResizableTh('approvalAt', 'Zatwierdzono')}
              {renderResizableTh('preview', null as any, 'center')}
              {renderResizableTh('actions', null as any, 'right')}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ p: 0.5, width: getColW('sel') }}>
                  {(r.prepared_by_avatar_url || r.prepared_by_name) ? (
                    <Tooltip title={r.prepared_by_name || ''} arrow>
                      <Avatar src={r.prepared_by_avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                        {(r.prepared_by_name || '').substring(0,1)}
                      </Avatar>
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell title={r.kzm_id || String(r.id)} sx={{ width: getColW('id') }}>{r.kzm_id || r.id}</TableCell>
                <TableCell sx={{ width: getColW('prepared') }}>{String(r.prepared_at || '').slice(0,10)}</TableCell>
                <TableCell sx={{ width: getColW('desc'), overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  <Tooltip title={r.material_name} arrow>
                    <span>{r.material_name}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: getColW('project'), overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  <Tooltip title={`${r.project_id} ${r.project_name || ''}`} arrow>
                    <span>{r.project_id} {r.project_name || ''}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ width: getColW('cardStatus') }}>{cardStatusChip(r.card_status || r.status)}</TableCell>
                <TableCell sx={{ width: getColW('cardStatusAt') }}>{r.card_status_at ? String(r.card_status_at).slice(0,10) : (r.status_at ? String(r.status_at).slice(0,10) : '')}</TableCell>
                <TableCell align="center" sx={{ width: getColW('approvalIcon') }}>{approvalStatusIcon(r.approval_status)}</TableCell>
                <TableCell sx={{ width: getColW('approvalAt') }}>{r.approval_status_at ? String(r.approval_status_at).slice(0,10) : ''}</TableCell>
                <TableCell align="center" sx={{ width: getColW('preview') }}>
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap: 0.5 }}>
                    {r.google_drive_file_url ? (
                      <>
                        <Tooltip title="Otwórz w Google Drive" arrow>
                          <IconButton size="small" onClick={()=>{ try { window.open(String(r.google_drive_file_url), '_blank'); } catch {} }}>
                            <img src={driveSvg} alt="Drive" style={{ width: 16, height: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Skopiuj link" arrow>
                          <IconButton size="small" onClick={()=>{ try { navigator.clipboard.writeText(String(r.google_drive_file_url)); } catch {} }}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : null}
                    <Tooltip title="Podgląd" arrow>
                      <IconButton size="small" onClick={()=>setPreview(r)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ pr: 0.5, width: getColW('actions') }}>
                  <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); setMenuRow(r); setMenuPosition({ top: e.clientY, left: e.clientX }); }}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ position: 'absolute', bottom: -26, right: -2, zIndex: 2 }}>
          <Tooltip title={colResizeMode ? 'Zablokuj zmianę szerokości kolumn' : 'Odblokuj zmianę szerokości kolumn'}>
            <IconButton size="small" onClick={() => setColResizeMode(v => !v)} color={colResizeMode ? 'primary' : 'default'}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {renderMenu()}

      <Dialog open={!!preview} onClose={()=>setPreview(null)} maxWidth="xl" fullWidth>
        <DialogTitle>{preview ? `Podgląd: ${preview.kzm_id || preview.id}` : 'Podgląd'}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {preview && (
            <Box sx={{ display:'flex', gap: 2, height: { xs: 'calc(100vh - 180px)', md: 'calc(100vh - 180px)' }, p: 2 }}>
              {/* Lewa – PDF viewer */}
              <Box sx={{ flex: 7, border: '2px dashed #9e9e9e', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
                {previewPdfUrl ? (
                  <iframe title="kzm-preview" src={previewPdfUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                ) : (
                  <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'text.secondary' }}>Brak podglądu PDF</Box>
                )}
              </Box>
              {/* Prawa – Szczegóły */}
              <Box sx={{ flex: 3, display:'grid', gridTemplateColumns:'160px 1fr', rowGap: 1, columnGap: 2, pr: 1 }}>
                <Typography variant="body2" color="text.secondary">Projekt</Typography>
                <Typography variant="body2">{preview.project_id} {preview.project_name || ''}</Typography>
                <Typography variant="body2" color="text.secondary">Branża</Typography>
                <Typography variant="body2">{preview.branch}</Typography>
                <Typography variant="body2" color="text.secondary">Przygotowano</Typography>
                <Typography variant="body2">{String(preview.prepared_at).slice(0,10)}</Typography>
                <Typography variant="body2" color="text.secondary">Wysłano</Typography>
                <Typography variant="body2">{preview.sent_at ? String(preview.sent_at).slice(0,10) : '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Status Karty</Typography>
                <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>{cardStatusChip(preview.card_status || preview.status)}<Typography variant="body2">{preview.card_status || preview.status || '—'}</Typography></Box>
                <Typography variant="body2" color="text.secondary">Data Statusu Karty</Typography>
                <Typography variant="body2">{preview.card_status_at ? String(preview.card_status_at).slice(0,10) : (preview.status_at ? String(preview.status_at).slice(0,10) : '—')}</Typography>
                <Typography variant="body2" color="text.secondary">Status zatwierdzenia</Typography>
                <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>{approvalStatusIcon(preview.approval_status)}<Typography variant="body2">{preview.approval_status || '—'}</Typography></Box>
                {/* Uwagi klienta (client_remarks) */}
                <Typography variant="body2" color="text.secondary">Uwagi do zatwierdzenia</Typography>
                <Typography variant="body2">{(preview as any)?.client_remarks || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Data statusu zatwierdzenia</Typography>
                <Typography variant="body2">{preview.approval_status_at ? String(preview.approval_status_at).slice(0,10) : '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Opis</Typography>
                <Typography variant="body2">{preview.material_name}</Typography>
                <Typography variant="body2" color="text.secondary">Przygotował</Typography>
                <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                  {preview.prepared_by_avatar_url ? (<Avatar src={preview.prepared_by_avatar_url} sx={{ width: 25, height: 25 }} />) : null}
                  <Typography variant="body2">{preview.prepared_by_name || '—'}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setPreview(null)}>Zamknij</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog wysyłki KZM */}
      <Dialog open={sendOpen} onClose={()=>setSendOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 2 }}>
            <span>Wyślij KZM {menuRow ? (menuRow.kzm_id || menuRow.id) : ''}</span>
            {sendBranch ? (<Chip label={sendBranch} size="small" color={branchChipColor(sendBranch)} />) : null}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {sendLoading && (
            <Box sx={{ mb: 2 }}><LinearProgress /></Box>
          )}
          <Box sx={{ display:'grid', gridTemplateColumns:{ xs: '1fr', md: '1fr' }, gap: 2 }}>
            <TextField select label="OD" size="small" value={sendFrom} onChange={(e)=>setSendFrom(String(e.target.value))}>
              <MenuItem value="powiadomienia@unitresco.com">powiadomienia@unitresco.com</MenuItem>
              <MenuItem value={(user as any)?.email || (user as any)?.login || ''}>{(user as any)?.email || (user as any)?.login || 'mój e‑mail'}</MenuItem>
            </TextField>
            <TextField label="DO" placeholder="adresy rozdzielane przecinkami" fullWidth size="small" value={sendTo} onChange={(e)=>setSendTo(e.target.value)} />
            <TextField label="DW" placeholder="DW (opcjonalnie)" fullWidth size="small" value={sendCc} onChange={(e)=>setSendCc(e.target.value)} />
            <TextField label="TEMAT" fullWidth size="small" value={sendSubject} onChange={(e)=>setSendSubject(e.target.value)} />
            <TextField label="TREŚĆ WIADOMOŚCI" fullWidth multiline minRows={6} value={sendBody} onChange={(e)=>setSendBody(e.target.value)} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Załączniki</Typography>
              <Divider sx={{ mb: 1 }} />
              {sendAttachments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Brak załączników</Typography>
              ) : (
                <FormControlLabel control={<Checkbox size="small" checked={sendIncludeAll} onChange={(e)=>setSendIncludeAll(e.target.checked)} />} label={`Załącz wszystkie (${sendAttachmentsSizeMB.toFixed(1)} MB)`} />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setSendOpen(false)} disabled={sendLoading}>Anuluj</Button>
          <Button variant="contained" onClick={async()=>{
            try {
              if (!menuRow) return;
              setSendLoading(true);
              await sendKzmEmail(menuRow.kzm_id || String(menuRow.id), { from: sendFrom, to: sendTo, cc: sendCc, subject: sendSubject, body: sendBody, includeAllAttachments: sendIncludeAll });
              setSendOpen(false);
              // Aktualizuj status lokalnie
              const now = new Date();
              const dateOnly = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
              setRows(prev => prev.map(r => r.id === menuRow.id ? { ...r, sent_at: r.sent_at || dateOnly, card_status: 'Wysłano do akceptacji' as any, card_status_at: r.card_status_at || dateOnly } : r));
              showSnack('success','KZM wysłany');
            } catch (e:any) {
              showSnack('error', e?.message || 'Błąd wysyłki KZM');
            } finally {
              setSendLoading(false);
            }
          }} disabled={sendLoading}>Wyślij</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog dodawania KZM */}
      <Dialog open={addOpen} onClose={()=>setAddOpen(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { width: '96vw', maxWidth: '96vw' } }}>
        <DialogTitle>{menuRow ? 'Edytuj kartę materiałową (KZM)' : 'Dodaj nową kartę materiałową (KZM)'}</DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
          {saving && (
            <Box sx={{ px: 2, pt: 1 }}>
              <LinearProgress color="primary" />
            </Box>
          )}
          <Box sx={{ display:'grid', gridTemplateColumns:'6fr 4fr', gap: 0, height: '100%' }}>
            {/* Podgląd/drag & drop */}
            <Box sx={{ p: 2, overflowY: 'auto', height: '100%' }}>
              <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, height: '68vh', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', backgroundColor: 'background.paper' }}>
                {activeAttachment ? (
                  activeAttachment?.driveId ? (
                    <iframe
                      key={activeAttachmentId || undefined}
                      src={`${API_BASE_URL}/kzms/attachments/${encodeURIComponent(activeAttachment.driveId)}/content`}
                      style={{ width: '100%', height: '100%', border: 0 }}
                      title={activeAttachment.name || 'Podgląd PDF'}
                    />
                  ) : (
                    <object key={activeAttachmentId || undefined} data={activeAttachment.url} type="application/pdf" width="100%" height="100%">
                      <Box sx={{ textAlign:'center', color:'text.secondary' }}>
                        <PictureAsPdf sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body2">Podgląd PDF niedostępny</Typography>
                      </Box>
                    </object>
                  )
                ) : (
                  <Box sx={{ textAlign:'center', color:'text.secondary' }}>
                    <UploadFile sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body1">Przeciągnij i upuść plik PDF tutaj</Typography>
                    <Typography variant="body2" sx={{ my: 1 }}>lub</Typography>
                    <Button variant="outlined" onClick={()=>fileInputRef.current?.click()}>Wybierz plik PDF</Button>
                  </Box>
                )}
                <input ref={fileInputRef} type="file" accept="application/pdf" multiple style={{ display:'none' }} onChange={(e)=>{ const files = Array.from(e.currentTarget.files || []); e.currentTarget.value=''; if (files.length) handleAddFiles(files as File[]); }} />
              </Box>

              {/* Lista załączników przeniesiona do panelu po prawej */}
            </Box>

            {/* Formularz boczny */}
            <Box sx={{ p: 2, borderLeft: '1px solid', borderColor: 'divider', backgroundColor: (t)=>t.palette.mode==='light'?'#fafafa':'rgba(255,255,255,0.04)', overflowY: 'auto', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color:'text.secondary' }}>Dane podstawowe</Typography>
              <Autocomplete size="small" sx={{ mb: 1.5 }} options={recentProjects} getOptionLabel={(p)=>`${p.id} ${p.name}`} value={recentProjects.find(p => p.id===addProjectId) || null} onChange={(_, val)=>setAddProjectId(val ? val.id : '')} renderInput={(params)=>(<TextField {...params} label="Projekt" placeholder="Projekt" error={!addProjectId} helperText={!addProjectId ? 'Wybierz projekt' : ''} />)} isOptionEqualToValue={(a,b)=>a.id===b.id} clearOnBlur={false} />
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }} error={!addBranch}>
                <Select value={addBranch} displayEmpty onChange={(e)=>setAddBranch((e.target.value || '') as any)} renderValue={(v)=> v || 'Branża'}>
                  <MenuItem value=""><em>Wybierz</em></MenuItem>
                  {branches.map(b => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
                </Select>
                {!addBranch && (<FormHelperText>Wybierz branżę</FormHelperText>)}
              </FormControl>
              <TextField label="Nazwa handlowa materiału" value={addMaterialName} onChange={(e)=>setAddMaterialName(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }} error={!addMaterialName.trim()} helperText={!addMaterialName.trim() ? 'Wpisz nazwę' : ''} />
              <TextField label="Miejsce wbudowania" value={addInstallPlace} onChange={(e)=>setAddInstallPlace(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }} error={!addInstallPlace.trim()} helperText={!addInstallPlace.trim() ? 'Wpisz miejsce' : ''} />
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }} error={!addCompliance}>
                <Select value={addCompliance} displayEmpty onChange={(e)=>setAddCompliance((e.target.value || '') as any)} renderValue={(v)=> v || 'Zgodność z projektem'}>
                  <MenuItem value=""><em>Wybierz</em></MenuItem>
                  <MenuItem value="Zgodny z projektem">Zgodny z projektem</MenuItem>
                  <MenuItem value="Równoważny">Równoważny</MenuItem>
                  <MenuItem value="Nie określony w projekcie">Nie określony w projekcie</MenuItem>
                </Select>
                {!addCompliance && (<FormHelperText>Wybierz zgodność</FormHelperText>)}
              </FormControl>

              {/* Załączniki z edycją nazw, usuwaniem i wyborem podglądu */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color:'text.secondary' }}>Załączniki</Typography>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={()=>fileInputRef.current?.click()} sx={{ mb: 1 }}>Dodaj z dysku</Button>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', display:'grid', gap: 1 }}>
                {attachments.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Brak załączników</Typography>
                )}
                {attachments.map((a, i) => (
                  <Box key={a.id} onClick={()=>setActiveAttachmentId(a.id)} sx={{ display:'grid', gridTemplateColumns:'auto 1fr auto auto', gap: 1, alignItems:'center', p: 1, border: '1px solid', borderColor: a.id===activeAttachmentId ? 'primary.main' : 'divider', borderRadius: 1, bgcolor: a.id===activeAttachmentId ? 'action.selected' : 'background.paper', cursor:'pointer', '&:hover':{ bgcolor: a.id===activeAttachmentId ? 'action.selected' : 'action.hover' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 34 }}>{`Z${i+1}:`}</Typography>
                    <TextField size="small" placeholder="Opis (np. numer dokumentu)" value={a.description || ''} onClick={(e)=>e.stopPropagation()} onChange={(e)=> setAttachments(prev => prev.map(x => x.id === a.id ? { ...x, description: e.target.value } : x))} />
                    <IconButton size="small" onClick={()=>setActiveAttachmentId(a.id)} title="Pokaż w podglądzie">
                      <PictureAsPdf fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={(e)=>{
                      e.stopPropagation();
                      setAttachments(prev => {
                        const next = prev.filter(x => x.id !== a.id);
                        if (a.id === activeAttachmentId) {
                          setActiveAttachmentId(next[0]?.id || null);
                        }
                        return next;
                      });
                    }} title="Usuń">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent:'space-between', alignItems:'center', px: 2 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap: 1, color: 'text.secondary' }} />
          <Box>
            {menuRow && (
              <Button color="error" onClick={async()=>{
                if (!menuRow) return;
                const confirm = window.confirm(`Usunąć KZM ${menuRow.kzm_id || menuRow.id}?\nUwaga: folder na Google Drive należy usunąć ręcznie (brak uprawnień aplikacji).`);
                if (!confirm) return;
                try {
                  setSaving(true);
                  await deleteKzm(menuRow.kzm_id || String(menuRow.id));
                  setAddOpen(false);
                  const list = await listMaterialApprovals();
                  setRows(list || []);
                  showSnack('success', 'KZM usunięty (folder na Drive usuń ręcznie)');
                } catch (e) {
                  showSnack('error', 'Błąd usuwania KZM');
                } finally {
                  setSaving(false);
                }
              }}>Usuń</Button>
            )}
            <Button onClick={()=>setAddOpen(false)} disabled={saving} sx={{ ml: 1 }}>Anuluj</Button>
            <Button variant="contained" disabled={saving} sx={{ ml: 1 }} onClick={async()=>{
              try {
                setSaving(true);
                if (!addProjectId) { showSnack('error','Wybierz projekt'); return; }
                if (!addBranch) { showSnack('error','Wybierz branżę'); return; }
                if (!addMaterialName.trim()) { showSnack('error','Wpisz nazwę handlową materiału'); return; }
                if (!addInstallPlace.trim()) { showSnack('error','Wpisz miejsce wbudowania'); return; }
                if (!addCompliance) { showSnack('error','Wybierz zgodność z projektem'); return; }
                if (menuRow) {
                  // Zapisz jako rewizję
                  showSnack('info', 'Zapisywanie rewizji…');
                  const keepDriveIds = attachments
                    .filter((a:any) => !!a.driveId)
                    .map((a:any) => ({ driveId: a.driveId as string, name: a.name as string, description: a.description as string }));
                  const newFiles = attachments
                    .filter((a:any) => !a.driveId && a.fileObject)
                    .map((a:any) => ({ file: a.fileObject as File, name: a.name as string, description: a.description as string }));
                  const res = await reviseKzm(menuRow.kzm_id || String(menuRow.id), {
                    material_trade_name: addMaterialName,
                    install_place: addInstallPlace,
                    compliance: addCompliance as any,
                    status: 'Przygotowany'
                  }, { keepDriveIds, newFiles, googleAccessToken: (user as any)?.google_access_token });
                  setAddOpen(false);
                  setResetAddOnOpen(true);
                  const list = await listMaterialApprovals();
                  setRows(list || []);
                  showSnack('success', `Utworzono rewizję: ${res?.kzm_id || ''}`);
                } else {
                  // Tworzenie nowego KZM (szybkie)
                  showSnack('info', 'Zapisywanie KZM…');
                  const files = attachments.map((a:any)=>a.fileObject).filter(Boolean) as File[];
                  const descriptions = attachments.filter((a:any)=>a.fileObject).map((a:any)=> a.description || '');
                  await quickCreateKzmUpload({ project_id: addProjectId, material_branch: addBranch as string, files, material_trade_name: addMaterialName, install_place: addInstallPlace, compliance: addCompliance || undefined }, (user as any)?.google_access_token, descriptions);
                  setAddOpen(false);
                  setResetAddOnOpen(true);
                  const list = await listMaterialApprovals();
                  setRows(list || []);
                  showSnack('success', 'KZM zapisany');
                }
              } catch (err) {
                console.error('Błąd zapisu KZM', err);
                showSnack('error', 'Błąd zapisu KZM');
              } finally {
                setSaving(false);
              }
            }}>{saving ? (menuRow ? 'Zapisywanie rewizji…' : 'Zapisywanie…') : (menuRow ? 'Zapisz rewizję' : 'Zapisz') }</Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ mt: 7 }}>
        <Alert elevation={3} onClose={()=>setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaterialApprovalPage;


